import React, { createContext, useState, useMemo, useCallback } from 'react';

export enum TemplateParameterTypeEnum {
	TEXT = 'TEXT',
	NUMBER = 'NUMBER',
	BOOLEAN = 'BOOLEAN',
}

export interface TemplateParameterValueType {
	type: TemplateParameterTypeEnum;
	value: string | number | boolean;
}

interface InContextRuleBuilderContextType {
	templateApiParameters: {
		[key: string]: TemplateParameterValueType;
	};
	setParameterByKey: (key: string, parameter: TemplateParameterValueType) => void;
	removeParameterByKey: (key: string) => void;
}

const defaultContext = {
	templateApiParameters: {},
	setParameterByKey: () => {},
	removeParameterByKey: () => {},
};

export const InContextRuleBuilderContext: React.Context<InContextRuleBuilderContextType> =
	createContext<InContextRuleBuilderContextType>(defaultContext);

export const InContextRuleBuilderProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const [parameters, setParameters] = useState<Record<string, TemplateParameterValueType>>({});

	const setParameterByKey = useCallback(
		(key: string, value: TemplateParameterValueType) => {
			setParameters((prev) => ({ ...prev, [key]: value }));
		},
		[setParameters],
	);

	const removeParameterByKey = useCallback(
		(key: string) => {
			setParameters((prev) => {
				const newParameters = { ...prev };
				delete newParameters[key];
				return newParameters;
			});
		},
		[setParameters],
	);

	const contextValue = useMemo(
		() => ({
			templateApiParameters: parameters,
			setParameterByKey,
			removeParameterByKey,
		}),
		[parameters, setParameterByKey, removeParameterByKey],
	);

	return (
		<InContextRuleBuilderContext.Provider value={contextValue}>
			{children}
		</InContextRuleBuilderContext.Provider>
	);
};
